import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { PageHeader } from '@/components/PageHeader';
import { Pill, SearchSelect, type PickOption } from '@/components/csp/CspKit';
import { useCspCompanies, useCspMappings, useCspProducts, useSaveCspMappings } from '@/api/csp';
import { normName, termClass, useCspRows } from '@/lib/csp';
import { cn } from '@/lib/utils';

/**
 * CSP → Mappings. Match the report's customer and product names to Autotask companies and
 * MSNCE services, used when a request's contract line is resolved. Rows auto-match by name;
 * override any of them, or set "No Autotask match" (kept, so auto-match stops suggesting it).
 * The source side is the uploaded report until the Synnex API is connected.
 */

type Kind = 'clients' | 'products';
const NO_MATCH = '__none__';

export default function CspMappingsPage() {
  const { visible, all } = useCspRows();
  const companies = useCspCompanies();
  const products = useCspProducts();
  const mappings = useCspMappings();
  const save = useSaveCspMappings();
  const [kind, setKind] = useState<Kind>('clients');
  const [q, setQ] = useState('');
  const [pending, setPending] = useState<Record<Kind, Record<string, number | null>>>({ clients: {}, products: {} });

  // Source names: CSV customers, and "Plan — Commitment" so monthly vs yearly aren't confused.
  const sources = useMemo(() => ({
    clients: [...new Set(all.map((r) => r.customer).filter(Boolean))].sort(),
    products: [...new Set(all.filter((r) => r.plan).map((r) => (r.commitment ? `${r.plan} — ${r.commitment}` : r.plan)))].sort(),
  }), [all]);

  const options = useMemo<Record<Kind, PickOption[]>>(() => ({
    clients: [{ value: NO_MATCH, label: '— No Autotask match —' }, ...(companies.data ?? []).map((c) => ({ value: String(c.id), label: c.name, hint: `#${c.id}` }))],
    products: [{ value: NO_MATCH, label: '— No Autotask match —' }, ...(products.data?.products ?? []).map((p) => ({ value: String(p.atServiceId), label: `${p.name}${p.term ? ` (${p.term})` : ''}`, hint: p.sku }))],
  }), [companies.data, products.data]);

  const autoMatch = (k: Kind, name: string): number | null => {
    if (k === 'clients') {
      const n = normName(name);
      if (!n) return null;
      const list = companies.data ?? [];
      return (list.find((c) => normName(c.name) === n) ?? list.find((c) => { const cn = normName(c.name); return !!cn && (cn.includes(n) || n.includes(cn)); }))?.id ?? null;
    }
    const sep = name.indexOf(' — ');
    const plan = sep >= 0 ? name.slice(0, sep) : name;
    const tc = termClass(sep >= 0 ? name.slice(sep + 3) : '');
    const n = normName(plan);
    if (!n) return null;
    const list = products.data?.products ?? [];
    const byName = list.filter((p) => normName(p.name) === n);
    return ((tc ? byName.find((p) => termClass(p.term) === tc) : undefined) ?? byName[0]
      ?? list.find((p) => { const pn = normName(p.name); return !!pn && (pn.includes(n) || n.includes(pn)); }))?.atServiceId ?? null;
  };

  const effective = (k: Kind, name: string): { id: number | null; origin: 'manual' | 'saved' | 'auto' | 'none' } => {
    if (name in pending[k]) return { id: pending[k][name], origin: 'manual' };
    const saved = mappings.data?.[k];
    if (saved && name in saved) return { id: saved[name], origin: 'saved' };
    const id = autoMatch(k, name);
    return { id, origin: id != null ? 'auto' : 'none' };
  };

  const dirty = Object.keys(pending.clients).length + Object.keys(pending.products).length;
  const rows = sources[kind].filter((n) => !q.trim() || n.toLowerCase().includes(q.trim().toLowerCase()));
  const loading = companies.isLoading || products.isLoading || mappings.isLoading;

  async function onSave() {
    try {
      await save.mutateAsync(pending);
      setPending({ clients: {}, products: {} });
      toast.success(`Saved ${dirty} mapping${dirty === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(`Could not save: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1300px] px-4 py-5 md:px-6">
      <PageHeader title="Mappings" count={rows.length} isFetching={mappings.isFetching}
        subtitle="Match report customers and products to Autotask. Auto-matched by name — override any row, or set “No Autotask match”."
        actions={<>
          <SearchInput withIcon placeholder="Filter…" className="w-56" value={q} onValueChange={setQ} />
          <Button size="sm" onClick={onSave} disabled={!dirty || save.isPending}>
            {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Save changes{dirty ? ` (${dirty})` : ''}
          </Button>
        </>}
      />

      <div className="mt-3 flex items-center gap-3">
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          {(['clients', 'products'] as const).map((k) => (
            <button key={k} type="button" onClick={() => setKind(k)}
              className={cn('rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors',
                kind === k ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
              {k} <span className="ml-1 tabular-nums text-muted-foreground">{sources[k].length}</span>
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">Source: the uploaded report{visible.length !== all.length ? ' (incl. long-expired)' : ''} — until the Synnex API is connected.</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading Autotask clients and services…</div>
      ) : (
        <div className="mt-3 rounded-lg border border-border bg-card shadow-sm">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
            <colgroup><col style={{ width: 36 }} /><col /><col style={{ width: '45%' }} /><col style={{ width: 100 }} /></colgroup>
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2" />
                <th className="px-3 py-2 font-medium">Report {kind === 'clients' ? 'customer' : 'product'}</th>
                <th className="px-3 py-2 font-medium">Autotask {kind === 'clients' ? 'company' : 'service (MSNCE)'}</th>
                <th className="px-3 py-2 font-medium">Match</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">{sources[kind].length ? 'No matches.' : 'Upload a report first.'}</td></tr>}
              {rows.map((name) => {
                const { id, origin } = effective(kind, name);
                return (
                  <tr key={name}>
                    <td className="px-3 py-1.5"><span className={cn('block h-2 w-2 rounded-full', id != null ? 'bg-success' : 'bg-destructive')} /></td>
                    <td className="truncate px-3 py-1.5 text-foreground" title={name}>{name}</td>
                    <td className="px-3 py-1.5">
                      <SearchSelect options={options[kind]} value={id != null ? String(id) : origin === 'none' ? '' : NO_MATCH}
                        placeholder="— No Autotask match —"
                        onChange={(v) => setPending((p) => ({ ...p, [kind]: { ...p[kind], [name]: !v || v === NO_MATCH ? null : Number(v) } }))} />
                    </td>
                    <td className="px-3 py-1.5">
                      <Pill tone={origin === 'saved' ? 'good' : origin === 'none' ? 'bad' : origin === 'manual' ? 'warn' : 'muted'}>
                        {origin === 'manual' ? 'Unsaved' : origin}
                      </Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
